import React, { useEffect, useState, useRef } from 'react';
import { useHistory, useLocation } from 'react-router-dom';
import { saveAs } from 'file-saver';
import Papa from 'papaparse';
import JSZip from 'jszip';
import moment from 'moment';
import { formatCurrency2SigFig } from '../../../../utils/campaigns';

const BasketItemsTabController = ({
  campaignOrder,
  pageSize,
  basketItemsData,
  basketItemsGroupedOrderId,
  basketItemsGroupedTotalCount,
  currentPage,
  basketItemsDataLoading,
  basketItemsByDate,
  setExportButtonMenuItems,
  setExportButtonLoading,
  setExportButtonDisable,
  ...props
}) => {
  const history = useHistory();
  const location = useLocation();
  const queryParams = new URLSearchParams(location.search);
  const basketItemsDataRef = useRef(basketItemsData);

  // Initialize date range from URL params or campaign dates
  const initialStartDate = queryParams.get('startDate')
    ? moment(queryParams.get('startDate'))
    : moment(campaignOrder.startDate);
  const initialEndDate = queryParams.get('endDate')
    ? moment(queryParams.get('endDate'))
    : moment(campaignOrder.endDate);

  const [dateRange, setDateRange] = useState([initialStartDate, initialEndDate]);
  const [expandedRowKeys, setExpandedRowKeys] = useState([]);
  const [searchTerm, setSearchTerm] = useState(queryParams.get('searchTerm') || '');
  const [basketItemsSearchTerms, setBasketItemsSearchTerms] = useState([]);

  // Filter basketItemsByDate based on date range
  const filteredBasketItemsByDate = basketItemsByDate
    ? basketItemsByDate.filter((item) => {
        const itemDate = moment(item.date);
        return (
          itemDate.isSameOrAfter(dateRange[0], 'day') &&
          itemDate.isSameOrBefore(dateRange[1], 'day')
        );
      })
    : [];

  // Consolidate basket items by name
  const consolidatedBasketItems = basketItemsData
    ? Object.values(
        basketItemsData.reduce((acc, item) => {
          const key = item.name;
          if (!acc[key]) {
            acc[key] = {
              id: item.id,
              name: item.name,
              quantity: 0,
              price: item.price,
              total: 0,
            };
          }
          acc[key].quantity += item.quantity;
          acc[key].total += item.total;
          return acc;
        }, {})
      )
    : [];

  // Filter basket items based on multiple search terms (tags) - OR logic
  const filteredBasketItems =
    basketItemsSearchTerms.length > 0
      ? consolidatedBasketItems.filter((item) => {
          const itemName = (item.name || '').toLowerCase();
          return basketItemsSearchTerms.some((term) => itemName.includes(term.toLowerCase()));
        })
      : consolidatedBasketItems;

  // Aggregate summary values from filtered basketItemsByDate
  const detailBarSummary =
    filteredBasketItemsByDate.length > 0
      ? filteredBasketItemsByDate.reduce(
          (acc, item) => {
            acc.transactions += item.transactions || 0;
            acc.revenue += item.revenue || 0;
            acc.quantity += item.quantity || 0;
            return acc;
          },
          { transactions: 0, revenue: 0, quantity: 0 }
        )
      : { transactions: 0, revenue: 0, quantity: 0 };

  // Keep ref updated with latest data
  useEffect(() => {
    basketItemsDataRef.current = basketItemsData;
  }, [basketItemsData]);

  // Set up export button only once on mount
  useEffect(() => {
    setExportButtonDisable(false);
    setExportButtonMenuItems([
      {
        key: 'basketItems',
        text: 'Basket Items',
        confirmTitle: 'Please allow Popup',
        confirmDescription: 'Popup should be allowed on your browser to download this report',
        hasConfirm: true,
        onClick: () => {
          setExportButtonLoading(true);
          const zip = new JSZip();
          const folder = zip.folder('Basket Items');

          folder.file(
            `${campaignOrder.name} Basket Items.csv`,
            Papa.unparse(
              (basketItemsDataRef.current || []).map((b) => ({
                Name: b.name,
                Quantity: b.quantity,
                Price: formatCurrency2SigFig(b.price),
                Total: formatCurrency2SigFig(b.total),
              }))
            )
          );
          zip.generateAsync({ type: 'blob' }).then(
            (blob) => {
              saveAs(blob, `${campaignOrder.name.replace(/\//g, ' ')} - Basket Items`);
              setExportButtonLoading(false);
              return true;
            },
            (e) => {
              console.log('error', e);
              setExportButtonLoading(false);
              return false;
            }
          );
        },
      },
    ]);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  const handleDateChange = (dates) => {
    if (dates && dates.length === 2) {
      setDateRange(dates);

      // Update URL params to trigger loader refetch
      queryParams.set('startDate', dates[0].format('YYYY-MM-DD'));
      queryParams.set('endDate', dates[1].format('YYYY-MM-DD'));
      queryParams.set('page', '1'); // Reset to page 1 on date change
      history.replace({
        pathname: location.pathname,
        search: queryParams.toString(),
      });
      // Reset expanded rows when date changes
      setExpandedRowKeys([]);
    }
  };

  const handlePageChange = (page, newPageSize) => {
    // When page size changes, snap back to page 1 so we don't land past the
    // new last page. Otherwise just persist the page index. Always persist
    // the size so the loader's URL-driven `pageSize` reflects the dropdown.
    const sizeChanged = newPageSize && newPageSize !== pageSize;
    queryParams.set('page', sizeChanged ? '1' : page.toString());
    if (newPageSize) {
      queryParams.set('pageSize', newPageSize.toString());
    }
    history.replace({
      pathname: location.pathname,
      search: queryParams.toString(),
    });
    // Reset expanded rows when page changes
    setExpandedRowKeys([]);
  };

  const handleExpand = (expanded, record) => {
    const keys = expanded
      ? [...expandedRowKeys, record.id]
      : expandedRowKeys.filter((key) => key !== record.id);
    setExpandedRowKeys(keys);
  };

  const handleSearchChange = (e) => {
    setSearchTerm(e.target.value);
  };

  const handleSearch = (value) => {
    queryParams.set('page', '1'); // Reset to page 1 on search
    if (value) {
      queryParams.set('searchTerm', value);
    } else {
      queryParams.delete('searchTerm');
    }
    history.replace({
      pathname: location.pathname,
      search: queryParams.toString(),
    });
    // Reset expanded rows when searching
    setExpandedRowKeys([]);
  };

  const handleBasketItemsSearchChange = (values) => {
    setBasketItemsSearchTerms(values);
  };

  return React.cloneElement(props.children, {
    campaignOrder,
    pageSize,
    basketItemsData: filteredBasketItems,
    basketItemsGroupedOrderId,
    basketItemsGroupedTotalCount,
    currentPage,
    basketItemsDataLoading,
    basketItemsByDate: filteredBasketItemsByDate,
    detailBarSummary,
    onDateChange: handleDateChange,
    onPageChange: handlePageChange,
    expandedRowKeys,
    onExpand: handleExpand,
    searchTerm,
    onSearchChange: handleSearchChange,
    onSearch: handleSearch,
    basketItemsSearchTerms,
    onBasketItemsSearchChange: handleBasketItemsSearchChange,
  });
};

export default BasketItemsTabController;
